import { getFeatureNameFromAccountType } from "@client-shared/providers/auth/SFDCAuthProvider";
import type { VendorDispatch } from "@client-shared/providers/vendor";
import { setVendor } from "@client-shared/providers/vendor";
import { getVendorForImpersonation } from "@client-shared/services/api";
import type { AccountType, Vendor, VendorProperties } from "@shared/types";

import { accountTypePermissionsMap } from "@bcc/sampleData/rbac-permissions";

/**
 * Enriches the vendor with featureName (derived from accountType) and
 * static default permissions (looked up from accountTypePermissionsMap)
 * so the vendor context matches what a normal OAuth login would produce.
 *
 * Permissions are assigned statically based on account type because the
 * backend /vendors/impersonate endpoint only returns { vendor, accountType }.
 * All vendors of the same account type share the same Salesforce permission set.
 */
const mergeRbacIntoVendor = (vendor: Vendor, accountType: AccountType): Vendor => {
  const featureName = getFeatureNameFromAccountType(accountType);
  const permissions = accountType in accountTypePermissionsMap ? accountTypePermissionsMap[accountType as AccountType] : undefined;

  const updatedProperties: VendorProperties = {
    ...vendor.properties,
    ...(featureName ? { featureName } : {}),
    ...(permissions ? { permissions } : {}),
  };

  return {
    ...vendor,
    properties: updatedProperties,
  };
};

export const fetchVendorDetailsAndSetVendor = async (vendorKey: string, vendorDispatch: VendorDispatch): Promise<Vendor> => {
  const { vendor, accountType } = await getVendorForImpersonation(vendorKey);
  const enrichedVendor = mergeRbacIntoVendor(vendor, accountType);
  vendorDispatch(setVendor(enrichedVendor));
  return enrichedVendor;
};
